import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Edit2, Save, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { updateDocument, deleteDocument } from '../api/mongoConsole'

interface Props {
  document: Record<string, unknown>
  database: string
  collection: string
  canWrite: boolean
  canDelete: boolean
  onClose: () => void
}

export function DocumentModal({ document, database, collection, canWrite, canDelete, onClose }: Props) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [editJson, setEditJson] = useState(JSON.stringify(document, null, 2))
  const [jsonError, setJsonError] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteReason, setDeleteReason] = useState('')

  const documentId = String(document._id ?? '')

  const updateMutation = useMutation({
    mutationFn: () => {
      const parsed = JSON.parse(editJson)
      return updateDocument(database, collection, documentId, { document: parsed, reason })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mongo-query'] })
      setEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteDocument(database, collection, documentId, deleteReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mongo-query'] })
      onClose()
    },
  })

  const handleJsonChange = (val: string) => {
    setEditJson(val)
    try { JSON.parse(val); setJsonError(null) } catch (e) { setJsonError('Invalid JSON') }
  }

  // Delete confirm sub-panel
  if (showDeleteConfirm) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
          <h3 className="text-lg font-semibold text-red-700 mb-2">Delete Document</h3>
          <p className="text-sm text-gray-600 mb-1">Document ID: <code className="font-mono text-xs bg-gray-100 px-1 rounded">{documentId}</code></p>
          <p className="text-sm font-medium text-red-600 mb-4">This action cannot be undone.</p>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
            <textarea
              value={deleteReason}
              onChange={e => setDeleteReason(e.target.value)}
              rows={2}
              placeholder="Why is this document being deleted?"
              className="w-full rounded-md border border-input px-3 py-2 text-sm resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          {deleteMutation.isError && (
            <p className="text-sm text-red-600 mb-3">{(deleteMutation.error as Error).message}</p>
          )}
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" disabled={!deleteReason.trim() || deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}>
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <div>
            <h3 className="font-semibold text-gray-900">{editing ? 'Edit Document' : 'Document'}</h3>
            <p className="text-xs text-gray-400 font-mono mt-0.5">{database}.{collection} / {documentId}</p>
          </div>
          <div className="flex items-center gap-2">
            {canDelete && !editing && (
              <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(true)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50">
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            )}
            {canWrite && !editing && (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Edit2 className="h-4 w-4 mr-1" /> Edit
              </Button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-1">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 min-h-0">
          {editing ? (
            <textarea
              value={editJson}
              onChange={e => handleJsonChange(e.target.value)}
              className={`w-full h-full min-h-[300px] font-mono text-xs bg-gray-900 text-green-300 rounded p-3 resize-none focus:outline-none focus:ring-2 focus:ring-primary ${jsonError ? 'ring-2 ring-red-500' : ''}`}
            />
          ) : (
            <pre className="font-mono text-xs text-gray-800 bg-gray-50 rounded p-3 overflow-auto whitespace-pre-wrap">
              {JSON.stringify(document, null, 2)}
            </pre>
          )}
        </div>

        {/* Edit footer */}
        {editing && (
          <div className="border-t p-4 shrink-0 space-y-3">
            {jsonError && <p className="text-xs text-red-600">{jsonError}</p>}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
              <input
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Why is this document being modified?"
                className="w-full rounded-md border border-input px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            {updateMutation.isError && (
              <p className="text-sm text-red-600">{(updateMutation.error as Error).message}</p>
            )}
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { setEditing(false); setEditJson(JSON.stringify(document, null, 2)) }}>
                Cancel
              </Button>
              <Button onClick={() => updateMutation.mutate()}
                disabled={!!jsonError || !reason.trim() || updateMutation.isPending}>
                <Save className="h-4 w-4 mr-1.5" />
                {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
